import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WorkflowCanvas
 *
 * SVG-based visual canvas for designing workflows via drag-and-drop.
 * Renders step nodes, connection lines, and handles all canvas interactions:
 * - Drag-and-drop: accept new steps from StepLibraryPanel, move existing steps
 * - Step selection, deletion, connection creation
 * - Zoom (mouse wheel / pinch) and pan (click-drag on empty canvas)
 * - Keyboard shortcuts: Delete, Ctrl+Z, Ctrl+Shift+Z, Ctrl+A
 * - Validation status indicators on each step node
 * - ARIA labels and screen reader descriptions
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 12.1–12.4, 12.6
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkflowBuilder } from '@/contexts/WorkflowBuilderContext';
import { calculateConnectionPath } from '@/utils/canvasLayout';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STEP_WIDTH = 200;
const STEP_HEIGHT = 80;
const STEP_BORDER_RADIUS = 8;
const PORT_RADIUS = 6;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.1;
/** Output port position relative to step origin. */
const OUTPUT_PORT = { x: STEP_WIDTH / 2, y: STEP_HEIGHT };
/** Input port position relative to step origin. */
const INPUT_PORT = { x: STEP_WIDTH / 2, y: 0 };
// ---------------------------------------------------------------------------
// Validation indicator colours
// ---------------------------------------------------------------------------
const VALIDATION_COLORS = {
    valid: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
};
const StepNode = ({ step, isSelected, onMouseDown, onOutputPortMouseDown, onInputPortMouseUp, }) => {
    const statusColor = VALIDATION_COLORS[step.validationStatus];
    const statusLabel = step.validationStatus === 'valid'
        ? 'Valid'
        : step.validationStatus === 'warning'
            ? `Warning: ${step.validationMessages[0] ?? 'has warnings'}`
            : `Error: ${step.validationMessages[0] ?? 'has errors'}`;
    return (_jsxs("g", { transform: `translate(${step.position.x}, ${step.position.y})`, role: "button", "aria-label": `Workflow step: ${step.name}. Type: ${step.type}. Status: ${statusLabel}`, tabIndex: 0, style: { cursor: 'grab' }, onMouseDown: (e) => onMouseDown(e, step.id), children: [isSelected && (_jsx("rect", { x: -4, y: -4, width: STEP_WIDTH + 8, height: STEP_HEIGHT + 8, rx: STEP_BORDER_RADIUS + 2, ry: STEP_BORDER_RADIUS + 2, fill: "none", stroke: "#3B82F6", strokeWidth: 2, strokeDasharray: "6 3", "aria-hidden": "true" })), _jsx("rect", { width: STEP_WIDTH, height: STEP_HEIGHT, rx: STEP_BORDER_RADIUS, ry: STEP_BORDER_RADIUS, fill: "#FFFFFF", stroke: isSelected ? '#3B82F6' : '#D1D5DB', strokeWidth: isSelected ? 2 : 1 }), _jsx("text", { x: 16, y: STEP_HEIGHT / 2 + 5, fontSize: 18, "aria-hidden": "true", children: step.type === 'agent_task' ? '🤖' : '⚙️' }), _jsx("text", { x: 40, y: STEP_HEIGHT / 2 - 4, fontSize: 13, fontWeight: 600, fill: "#1F2937", style: { userSelect: 'none' }, children: step.name.length > 18 ? `${step.name.slice(0, 18)}…` : step.name }), _jsx("text", { x: 40, y: STEP_HEIGHT / 2 + 14, fontSize: 11, fill: "#6B7280", style: { userSelect: 'none' }, children: step.type === 'agent_task' ? 'Agent Task' : 'Function' }), _jsx("circle", { cx: STEP_WIDTH - 16, cy: 16, r: 5, fill: statusColor, "aria-label": statusLabel, children: _jsx("title", { children: statusLabel }) }), _jsx("circle", { cx: INPUT_PORT.x, cy: INPUT_PORT.y, r: PORT_RADIUS, fill: "#E5E7EB", stroke: "#9CA3AF", strokeWidth: 1.5, style: { cursor: 'crosshair' }, onMouseUp: (e) => onInputPortMouseUp(e, step.id), "aria-label": `Input port for ${step.name}`, children: _jsx("title", { children: "Input port \u2014 drop a connection here" }) }), _jsx("circle", { cx: OUTPUT_PORT.x, cy: OUTPUT_PORT.y, r: PORT_RADIUS, fill: "#E5E7EB", stroke: "#9CA3AF", strokeWidth: 1.5, style: { cursor: 'crosshair' }, onMouseDown: (e) => {
                    e.stopPropagation();
                    onOutputPortMouseDown(e, step.id);
                }, "aria-label": `Output port for ${step.name}`, children: _jsx("title", { children: "Output port \u2014 drag to connect" }) })] }));
};
const ConnectionLine = ({ connection, steps }) => {
    const fromStep = steps.find((s) => s.id === connection.fromStepId);
    const toStep = steps.find((s) => s.id === connection.toStepId);
    if (!fromStep || !toStep)
        return null;
    const from = {
        x: fromStep.position.x + OUTPUT_PORT.x,
        y: fromStep.position.y + OUTPUT_PORT.y,
    };
    const to = {
        x: toStep.position.x + INPUT_PORT.x,
        y: toStep.position.y + INPUT_PORT.y,
    };
    const path = calculateConnectionPath(from, to);
    return (_jsx("path", { d: path, stroke: "#94A3B8", strokeWidth: 2, fill: "none", markerEnd: "url(#arrowhead)", "aria-label": `Connection from ${fromStep.name} to ${toStep.name}` }));
};
// ---------------------------------------------------------------------------
// Main canvas component
// ---------------------------------------------------------------------------
const WorkflowCanvas = () => {
    const { state, addStep, removeStep, moveStep, connectSteps, selectStep, setZoom, setPan, undo, redo, canUndo, canRedo, } = useWorkflowBuilder();
    const { steps, connections, selectedStepId, zoom, pan } = state.canvasState;
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    // -----------------------------------------------------------------------
    // Drag state: moving existing steps
    // -----------------------------------------------------------------------
    const [draggingStepId, setDraggingStepId] = useState(null);
    const dragOffset = useRef({ x: 0, y: 0 });
    // -----------------------------------------------------------------------
    // Pan state
    // -----------------------------------------------------------------------
    const [isPanning, setIsPanning] = useState(false);
    const panStart = useRef({ x: 0, y: 0 });
    const panOrigin = useRef({ x: 0, y: 0 });
    // -----------------------------------------------------------------------
    // Connection creation state
    // -----------------------------------------------------------------------
    const [connectingFrom, setConnectingFrom] = useState(null);
    const [tempConnectionEnd, setTempConnectionEnd] = useState(null);
    // -----------------------------------------------------------------------
    // Helpers: convert screen coords to canvas coords
    // -----------------------------------------------------------------------
    const screenToCanvas = useCallback((clientX, clientY) => {
        const svg = svgRef.current;
        if (!svg)
            return { x: 0, y: 0 };
        const rect = svg.getBoundingClientRect();
        return {
            x: (clientX - rect.left) / zoom - pan.x,
            y: (clientY - rect.top) / zoom - pan.y,
        };
    }, [zoom, pan]);
    // -----------------------------------------------------------------------
    // Drop handler: accept new steps from StepLibraryPanel
    // -----------------------------------------------------------------------
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        try {
            const raw = e.dataTransfer.getData('application/json');
            if (!raw)
                return;
            const data = JSON.parse(raw);
            if (data.type !== 'new-step' || !data.step)
                return;
            const position = screenToCanvas(e.clientX, e.clientY);
            addStep(data.step, position);
        }
        catch {
            // Ignore malformed drag data
        }
    }, [addStep, screenToCanvas]);
    // -----------------------------------------------------------------------
    // Step mouse-down: start moving or select
    // -----------------------------------------------------------------------
    const handleStepMouseDown = useCallback((e, stepId) => {
        e.stopPropagation();
        selectStep(stepId);
        const canvasPos = screenToCanvas(e.clientX, e.clientY);
        const step = steps.find((s) => s.id === stepId);
        if (!step)
            return;
        dragOffset.current = {
            x: canvasPos.x - step.position.x,
            y: canvasPos.y - step.position.y,
        };
        setDraggingStepId(stepId);
    }, [selectStep, screenToCanvas, steps]);
    // -----------------------------------------------------------------------
    // Output port mouse-down: start connection creation
    // -----------------------------------------------------------------------
    const handleOutputPortMouseDown = useCallback((e, stepId) => {
        e.stopPropagation();
        e.preventDefault();
        setConnectingFrom(stepId);
        const pos = screenToCanvas(e.clientX, e.clientY);
        setTempConnectionEnd(pos);
    }, [screenToCanvas]);
    // -----------------------------------------------------------------------
    // Input port mouse-up: complete connection
    // -----------------------------------------------------------------------
    const handleInputPortMouseUp = useCallback((_e, stepId) => {
        if (connectingFrom && connectingFrom !== stepId) {
            connectSteps(connectingFrom, stepId);
        }
        setConnectingFrom(null);
        setTempConnectionEnd(null);
    }, [connectingFrom, connectSteps]);
    // -----------------------------------------------------------------------
    // Canvas mouse-down: start panning (on empty area)
    // -----------------------------------------------------------------------
    const handleCanvasMouseDown = useCallback((e) => {
        // Only pan on left-click on empty canvas
        if (e.button !== 0)
            return;
        selectStep(null);
        setIsPanning(true);
        panStart.current = { x: e.clientX, y: e.clientY };
        panOrigin.current = { ...pan };
    }, [selectStep, pan]);
    // -----------------------------------------------------------------------
    // Global mouse-move: handle step dragging, panning, connection drawing
    // -----------------------------------------------------------------------
    useEffect(() => {
        const handleMouseMove = (e) => {
            // Step dragging
            if (draggingStepId) {
                const canvasPos = screenToCanvas(e.clientX, e.clientY);
                moveStep(draggingStepId, {
                    x: canvasPos.x - dragOffset.current.x,
                    y: canvasPos.y - dragOffset.current.y,
                });
                return;
            }
            // Connection drawing
            if (connectingFrom) {
                const pos = screenToCanvas(e.clientX, e.clientY);
                setTempConnectionEnd(pos);
                return;
            }
            // Panning
            if (isPanning) {
                const dx = (e.clientX - panStart.current.x) / zoom;
                const dy = (e.clientY - panStart.current.y) / zoom;
                setPan({
                    x: panOrigin.current.x + dx,
                    y: panOrigin.current.y + dy,
                });
            }
        };
        const handleMouseUp = () => {
            setDraggingStepId(null);
            setIsPanning(false);
            setConnectingFrom(null);
            setTempConnectionEnd(null);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        draggingStepId,
        connectingFrom,
        isPanning,
        zoom,
        screenToCanvas,
        moveStep,
        setPan,
    ]);
    // -----------------------------------------------------------------------
    // Zoom: mouse wheel
    // -----------------------------------------------------------------------
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const handleWheel = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
            const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + delta));
            setZoom(newZoom);
        };
        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [zoom, setZoom]);
    // -----------------------------------------------------------------------
    // Keyboard shortcuts
    // -----------------------------------------------------------------------
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle when canvas container or SVG is focused / active
            const active = document.activeElement;
            const container = containerRef.current;
            if (!container)
                return;
            // Skip if user is typing in an input/textarea
            if (active instanceof HTMLInputElement ||
                active instanceof HTMLTextAreaElement ||
                active instanceof HTMLSelectElement) {
                return;
            }
            // Delete selected step
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedStepId) {
                e.preventDefault();
                removeStep(selectedStepId);
                return;
            }
            // Ctrl+Z / Cmd+Z — undo
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
                e.preventDefault();
                if (canUndo)
                    undo();
                return;
            }
            // Ctrl+Shift+Z / Cmd+Shift+Z — redo
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
                e.preventDefault();
                if (canRedo)
                    redo();
                return;
            }
            // Ctrl+A / Cmd+A — select all (select first step as proxy)
            if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                e.preventDefault();
                if (steps.length > 0) {
                    selectStep(steps[0].id);
                }
                return;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedStepId, removeStep, undo, redo, canUndo, canRedo, selectStep, steps]);
    // -----------------------------------------------------------------------
    // Temporary connection line while dragging from output port
    // -----------------------------------------------------------------------
    const renderTempConnection = () => {
        if (!connectingFrom || !tempConnectionEnd)
            return null;
        const fromStep = steps.find((s) => s.id === connectingFrom);
        if (!fromStep)
            return null;
        const from = {
            x: fromStep.position.x + OUTPUT_PORT.x,
            y: fromStep.position.y + OUTPUT_PORT.y,
        };
        const path = calculateConnectionPath(from, tempConnectionEnd);
        return (_jsx("path", { d: path, stroke: "#3B82F6", strokeWidth: 2, strokeDasharray: "6 4", fill: "none", pointerEvents: "none", "aria-hidden": "true" }));
    };
    // -----------------------------------------------------------------------
    // Empty state
    // -----------------------------------------------------------------------
    const renderEmptyState = () => {
        if (steps.length > 0)
            return null;
        return (_jsx("text", { x: "50%", y: "50%", textAnchor: "middle", dominantBaseline: "middle", fill: "#9CA3AF", fontSize: 16, style: { userSelect: 'none' }, "aria-label": "Empty canvas. Drag steps from the library to get started.", children: "Drag steps from the library to get started" }));
    };
    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (_jsxs("div", { ref: containerRef, className: "flex-1 relative overflow-hidden bg-gray-100", onDragOver: handleDragOver, onDrop: handleDrop, role: "application", "aria-label": "Workflow canvas. Use mouse to drag steps, scroll to zoom, and keyboard shortcuts for editing.", "aria-roledescription": "Workflow design canvas", tabIndex: 0, children: [_jsxs("svg", { ref: svgRef, className: "w-full h-full", onMouseDown: handleCanvasMouseDown, style: { cursor: isPanning ? 'grabbing' : 'default' }, children: [_jsx("defs", { children: _jsx("marker", { id: "arrowhead", markerWidth: "10", markerHeight: "10", refX: "9", refY: "3", orient: "auto", markerUnits: "strokeWidth", children: _jsx("polygon", { points: "0 0, 10 3, 0 6", fill: "#94A3B8" }) }) }), _jsxs("g", { transform: `scale(${zoom}) translate(${pan.x}, ${pan.y})`, children: [connections.map((conn) => (_jsx(ConnectionLine, { connection: conn, steps: steps }, conn.id))), renderTempConnection(), steps.map((step) => (_jsx(StepNode, { step: step, isSelected: step.id === selectedStepId, onMouseDown: handleStepMouseDown, onOutputPortMouseDown: handleOutputPortMouseDown, onInputPortMouseUp: handleInputPortMouseUp }, step.id)))] }), renderEmptyState()] }), _jsxs("div", { className: "sr-only", role: "status", "aria-live": "polite", "aria-atomic": "true", children: [steps.length, " steps on canvas.", selectedStepId
                        ? ` Selected: ${steps.find((s) => s.id === selectedStepId)?.name ?? 'unknown'}`
                        : ''] })] }));
};
export default WorkflowCanvas;
